import type { NextPage } from 'next';
import { useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { Layout } from 'app/Layout';
import { fullDealOfferAction, sravniOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import { SeoComponents } from 'entities/Seo/ui/SeoComponents';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { AnchorLinkList } from 'shared/ui';
import { Container } from 'shared/ui/Container/Container';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';

import styles from './CalculatorPage.module.scss';
import { CalculatorPageHeader } from './CalculatorPageHeader';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.CALCULATOR,
  source: 'search',
  cardTermIsVisible: true,
  withCardProductLink: true,
  sortPanelVisible: true,
};

export const CalculatorPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname, hasUtmLabel } = useListPage(ListKeys.CALCULATOR);
  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);

  const hasSeoBlocks = !hasUtmLabel && Boolean(seo);

  useBestProducts();

  return (
    <Layout>
      <PageContext.Provider value={config}>
        <Container>
          <CalculatorPageHeader filters={filter.filters} />

          {!hasUtmLabel && <AnchorLinkList className={styles.anchorLinkList} />}

          <MobileSubHeader />
          <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
            <ProductList
              gridLayout
              filter={filter}
              pathname={pathname}
              renderCard={(props) => <LightweightCard hasUtmLabel={hasUtmLabel} {...props} />}
            />
          </CardActionProvider>
        </Container>
        {hasSeoBlocks && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={false}
            showSubheader={false}
          />
        )}
      </PageContext.Provider>
    </Layout>
  );
};

CalculatorPage.getInitialProps = (ctx: Application.ReduxNextPageContext) =>
  getPageListData(ctx, {
    key: ListKeys.CALCULATOR,
    defaultFiltersMode: MODES.CALCULATOR,
  });
